/**
 * Light-weight Markdown renderer for streaming Claude replies.
 *
 * Source references:
 *   - Loveable v1 reference: `src/components/xaes/AssistantMarkdown.tsx`
 *     uses `react-markdown` + `remark-gfm`. Those packages are listed
 *     in the rebuild plan as deps but are not yet installed in this
 *     repo's `package.json`, so this file ships a minimal in-house
 *     renderer that handles the GFM subset Claude actually streams:
 *     headings, paragraphs, bullets/numbered lists, fenced code,
 *     inline code, bold, italics, and inline links. Once the deps
 *     are added, swap this for `<ReactMarkdown remarkPlugins={[gfm]} />`
 *     and delete the parser below.
 *
 * The renderer is intentionally conservative — anything it doesn't
 * understand falls through as plain text. The chat bubble stays
 * readable mid-stream even when a Markdown construct is half-typed.
 */

import { memo, type ReactNode } from "react";
import { cn } from "@/lib/utils";

type Props = {
  text: string;
  className?: string;
};

/** Inline-formatting pass — handles bold, italic, inline code, links.
 *  Returns a `ReactNode[]` so a single line of text can mix several
 *  styles. */
function renderInline(input: string, keyPrefix: string): ReactNode[] {
  // Order matters: process inline code first so its contents are not
  // re-interpreted as bold / italic. Each pass walks the array of
  // ReactNode and replaces remaining string segments with new nodes.
  let nodes: ReactNode[] = [input];

  // Inline code — `…`
  nodes = splitNodes(nodes, /`([^`]+)`/g, (match, k) => (
    <code
      key={k}
      className="bg-secondary/60 text-foreground rounded px-1 py-0.5 font-mono text-[0.85em]"
    >
      {match[1]}
    </code>
  ));

  // Bold — **…**
  nodes = splitNodes(nodes, /\*\*([^*]+)\*\*/g, (match, k) => (
    <strong key={k} className="font-semibold">
      {match[1]}
    </strong>
  ));

  // Italic — _…_ (markdown also supports *…*, but Claude streams
  // bold via ** and italic via _; we match that convention to avoid
  // mis-splitting tokens like multiplication asterisks).
  nodes = splitNodes(nodes, /_([^_\n]+)_/g, (match, k) => (
    <em key={k} className="italic">
      {match[1]}
    </em>
  ));

  // Inline links — [label](url)
  nodes = splitNodes(nodes, /\[([^\]]+)\]\(([^)]+)\)/g, (match, k) => (
    <a
      key={k}
      href={match[2]}
      target="_blank"
      rel="noreferrer"
      className="text-true-blue underline-offset-2 hover:underline"
    >
      {match[1]}
    </a>
  ));

  return nodes.map((node, idx) =>
    typeof node === "string" ? <span key={`${keyPrefix}-t-${idx}`}>{node}</span> : node,
  );
}

/** Apply a regex pass to an existing `ReactNode[]`, splitting string
 *  members on each match and replacing them with the renderer's output. */
function splitNodes(
  input: ReactNode[],
  pattern: RegExp,
  render: (match: RegExpExecArray, key: string) => ReactNode,
): ReactNode[] {
  const out: ReactNode[] = [];
  let nodeIdx = 0;
  for (const node of input) {
    if (typeof node !== "string") {
      out.push(node);
      continue;
    }
    let lastIndex = 0;
    let matchIdx = 0;
    // Reset regex state because callers reuse the same instance.
    pattern.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = pattern.exec(node)) !== null) {
      if (m.index > lastIndex) {
        out.push(node.slice(lastIndex, m.index));
      }
      out.push(render(m, `n${nodeIdx}-m${matchIdx}`));
      lastIndex = m.index + m[0].length;
      matchIdx++;
      // Guard against zero-width matches looping forever.
      if (m.index === pattern.lastIndex) pattern.lastIndex++;
    }
    if (lastIndex < node.length) {
      out.push(node.slice(lastIndex));
    }
    nodeIdx++;
  }
  return out;
}

/** Parse a `text` string into block-level Markdown elements. Returns
 *  a flat array of React nodes so the caller doesn't need to think
 *  about list nesting or fenced-code state. */
function renderBlocks(text: string): ReactNode[] {
  const lines = text.split("\n");
  const out: ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i] ?? "";

    // Fenced code — ``` … ```
    if (line.trim().startsWith("```")) {
      const lang = line.trim().slice(3).trim();
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !(lines[i] ?? "").trim().startsWith("```")) {
        codeLines.push(lines[i] ?? "");
        i++;
      }
      // Skip the closing fence (or end-of-input if Claude is still typing).
      if (i < lines.length) i++;
      out.push(
        <pre
          key={`pre-${out.length}`}
          className="border-border/40 bg-secondary/60 my-2 overflow-x-auto rounded-md border p-2 font-mono text-[0.78rem] leading-snug"
        >
          <code className={lang ? `language-${lang}` : undefined}>
            {codeLines.join("\n")}
          </code>
        </pre>,
      );
      continue;
    }

    // Headings — `## Title` (we render `#`–`####` consistently).
    const headingMatch = /^(#{1,4})\s+(.*)$/.exec(line);
    if (headingMatch) {
      // noUncheckedIndexedAccess: regex match groups are typed as
      // possibly-undefined even when the pattern guarantees them.
      const hashes = headingMatch[1] ?? "";
      const body = headingMatch[2] ?? "";
      const level = hashes.length;
      const headingClass = cn(
        "mt-2 mb-1 font-semibold tracking-tight",
        level === 1 && "text-base",
        level === 2 && "text-sm",
        level >= 3 && "text-muted-foreground text-xs uppercase tracking-[0.16em]",
      );
      const inline = renderInline(body, `hi-${out.length}`);
      const key = `h-${out.length}`;
      // Render the right semantic level — falling back to h5 for deeper
      // nesting so we never emit invalid tag names.
      if (level === 1) {
        out.push(<h2 key={key} className={headingClass}>{inline}</h2>);
      } else if (level === 2) {
        out.push(<h3 key={key} className={headingClass}>{inline}</h3>);
      } else if (level === 3) {
        out.push(<h4 key={key} className={headingClass}>{inline}</h4>);
      } else {
        out.push(<h5 key={key} className={headingClass}>{inline}</h5>);
      }
      i++;
      continue;
    }

    // Bullet list — collect consecutive `- ` lines into a single <ul>.
    if (/^[-*]\s+/.test(line)) {
      const items: ReactNode[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i] ?? "")) {
        const body = (lines[i] ?? "").replace(/^[-*]\s+/, "");
        items.push(
          <li key={`li-${i}`} className="ml-4 list-disc">
            {renderInline(body, `lii-${i}`)}
          </li>,
        );
        i++;
      }
      out.push(
        <ul key={`ul-${out.length}`} className="my-1 flex flex-col gap-0.5">
          {items}
        </ul>,
      );
      continue;
    }

    // Numbered list — `1. Foo`.
    if (/^\d+\.\s+/.test(line)) {
      const items: ReactNode[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i] ?? "")) {
        const body = (lines[i] ?? "").replace(/^\d+\.\s+/, "");
        items.push(
          <li key={`oli-${i}`} className="ml-4 list-decimal">
            {renderInline(body, `olii-${i}`)}
          </li>,
        );
        i++;
      }
      out.push(
        <ol key={`ol-${out.length}`} className="my-1 flex flex-col gap-0.5">
          {items}
        </ol>,
      );
      continue;
    }

    // Blank line — paragraph separator.
    if (line.trim() === "") {
      i++;
      continue;
    }

    // Paragraph — gather consecutive non-blank, non-block lines.
    const paraLines: string[] = [line];
    i++;
    while (
      i < lines.length &&
      (lines[i] ?? "").trim() !== "" &&
      !/^[-*]\s+/.test(lines[i] ?? "") &&
      !/^\d+\.\s+/.test(lines[i] ?? "") &&
      !/^#{1,4}\s+/.test(lines[i] ?? "") &&
      !(lines[i] ?? "").trim().startsWith("```")
    ) {
      paraLines.push(lines[i] ?? "");
      i++;
    }
    out.push(
      <p key={`p-${out.length}`} className="my-1 leading-relaxed">
        {renderInline(paraLines.join(" "), `pi-${out.length}`)}
      </p>,
    );
  }

  return out;
}

function AssistantMarkdownImpl({ text, className }: Props) {
  return (
    <div className={cn("text-sm text-foreground/90", className)}>
      {renderBlocks(text)}
    </div>
  );
}

/** Memoised — the markdown re-render cost is the only thing that
 *  ever shows up in DevTools' Profiler during a long stream. */
export const AssistantMarkdown = memo(AssistantMarkdownImpl);
